import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, CalendarDays, Inbox, Pencil, Trash2, Plus, X } from "lucide-react";
import { ZenLoader, Eyebrow } from "../components/Reveal";

const API = process.env.REACT_APP_BACKEND_URL;
const CATEGORIES = ["Meditazione", "Yoga", "Cucina Consapevole", "Trattamenti", "Percorsi", "Ritiro", "Altro"];
const EMPTY = { title: "", description: "", date: "", time: "", location: "Studio Artemisia", category: "Meditazione", price: "", spots: 10 };

const inputCls =
  "w-full rounded-2xl bg-cream-dark/60 px-5 py-3.5 text-sm text-ink placeholder:text-ink/35 outline-none transition-shadow duration-300 focus:shadow-[0_0_0_3px_rgba(79,100,91,0.18)]";
const labelCls = "mb-2 block text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-ink/50";

const Admin = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("artemisia_admin_token");
  const [tab, setTab] = useState("eventi");
  const [bookings, setBookings] = useState(null);
  const [events, setEvents] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const authFetch = useCallback(
    async (path, options = {}) => {
      const res = await fetch(`${API}${path}`, {
        ...options,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) {
        localStorage.removeItem("artemisia_admin_token");
        navigate("/admin/login");
        throw new Error("unauthorized");
      }
      return res;
    },
    [token, navigate]
  );

  const load = useCallback(async () => {
    try {
      const [b, e] = await Promise.all([authFetch("/api/admin/bookings"), authFetch("/api/events")]);
      setBookings(await b.json());
      setEvents(await e.json());
    } catch {}
  }, [authFetch]);

  useEffect(() => {
    if (!token) {
      navigate("/admin/login");
      return;
    }
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const logout = () => {
    localStorage.removeItem("artemisia_admin_token");
    navigate("/admin/login");
  };

  const saveEvent = async (e) => {
    e.preventDefault();
    setSaving(true);
    const isEdit = Boolean(form.id);
    try {
      const res = await authFetch(isEdit ? `/api/admin/events/${form.id}` : "/api/admin/events", {
        method: isEdit ? "PUT" : "POST",
        body: JSON.stringify({ ...form, spots: Number(form.spots) }),
      });
      if (!res.ok) throw new Error();
      toast.success(isEdit ? "Evento aggiornato." : "Evento aggiunto al calendario.");
      setForm(null);
      load();
    } catch {
      toast.error("Non riesco a salvare l'evento. Riprova.");
    } finally {
      setSaving(false);
    }
  };

  const deleteEvent = async (id) => {
    if (!window.confirm("Eliminare questo evento dal calendario?")) return;
    try {
      await authFetch(`/api/admin/events/${id}`, { method: "DELETE" });
      toast.success("Evento eliminato.");
      load();
    } catch {
      toast.error("Eliminazione non riuscita.");
    }
  };

  const deleteBooking = async (id) => {
    if (!window.confirm("Eliminare questa richiesta?")) return;
    try {
      await authFetch(`/api/admin/bookings/${id}`, { method: "DELETE" });
      toast.success("Richiesta eliminata.");
      load();
    } catch {
      toast.error("Eliminazione non riuscita.");
    }
  };

  const loading = bookings === null || events === null;

  return (
    <div data-testid="admin-dashboard" className="mx-auto max-w-5xl px-6 pt-36 pb-10">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <Eyebrow>Area Riservata</Eyebrow>
          <h1 className="mt-4 font-serif text-4xl italic" data-testid="admin-title">Il tuo spazio, Agnese</h1>
        </div>
        <button
          onClick={logout}
          data-testid="admin-logout"
          className="flex items-center gap-2 rounded-full bg-cream-soft px-6 py-3 text-sm text-ink/60 transition-colors hover:text-sage-deep"
        >
          <LogOut className="h-4 w-4" /> Esci
        </button>
      </div>

      <div className="mt-10 flex gap-3">
        {[
          ["eventi", "Calendario Eventi", CalendarDays],
          ["prenotazioni", `Richieste (${bookings?.length ?? "…"})`, Inbox],
        ].map(([key, label, Icon]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            data-testid={`tab-${key}`}
            className={`flex items-center gap-2.5 rounded-full px-6 py-3.5 text-sm font-medium transition-all duration-300 ${
              tab === key ? "bg-sage text-cream" : "bg-cream-soft text-ink/60 hover:text-sage-deep"
            }`}
          >
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      {loading && <ZenLoader />}

      {!loading && tab === "eventi" && (
        <div className="mt-10">
          {!form && (
            <button
              onClick={() => setForm({ ...EMPTY })}
              data-testid="event-add-button"
              className="mb-8 flex items-center gap-2.5 rounded-full bg-[linear-gradient(135deg,#4F645B,#7A9488)] px-7 py-3.5 text-sm font-semibold text-cream transition-transform duration-500 hover:scale-105"
            >
              <Plus className="h-4 w-4" /> Nuovo evento
            </button>
          )}

          {form && (
            <form onSubmit={saveEvent} data-testid="event-form" className="mb-10 rounded-[2.5rem] bg-cream-soft p-8 sm:p-10">
              <div className="flex items-center justify-between">
                <h2 className="font-serif text-2xl italic">{form.id ? "Modifica evento" : "Nuovo evento"}</h2>
                <button type="button" onClick={() => setForm(null)} data-testid="event-form-close" className="flex h-10 w-10 items-center justify-center rounded-full bg-cream-dark/60 text-ink/50">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="mt-7 grid gap-5 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className={labelCls}>Titolo</label>
                  <input required data-testid="event-form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Es. Yoga Dolce al Mattino" className={inputCls} />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls}>Descrizione</label>
                  <textarea required rows={3} data-testid="event-form-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={`${inputCls} resize-none`} />
                </div>
                <div>
                  <label className={labelCls}>Data</label>
                  <input required type="date" data-testid="event-form-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Orario</label>
                  <input required data-testid="event-form-time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} placeholder="18:30 – 20:00" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Luogo</label>
                  <input required data-testid="event-form-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Categoria</label>
                  <select data-testid="event-form-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={`${inputCls} appearance-none`}>
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Prezzo</label>
                  <input required data-testid="event-form-price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="€ 25" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Posti disponibili</label>
                  <input required type="number" min="1" data-testid="event-form-spots" value={form.spots} onChange={(e) => setForm({ ...form, spots: e.target.value })} className={inputCls} />
                </div>
              </div>
              <button
                type="submit"
                disabled={saving}
                data-testid="event-save-button"
                className="mt-8 rounded-full bg-[linear-gradient(135deg,#4F645B,#7A9488)] px-8 py-4 text-sm font-semibold text-cream transition-all duration-500 hover:scale-[1.03] disabled:opacity-60"
              >
                {saving ? "Salvataggio…" : form.id ? "Salva modifiche" : "Aggiungi al calendario"}
              </button>
            </form>
          )}

          <div className="space-y-4" data-testid="admin-events-list">
            {events.length === 0 && <p className="rounded-[2rem] bg-cream-soft p-8 text-center text-sm text-ink/50">Nessun evento in calendario. Aggiungine uno!</p>}
            {events.map((e) => (
              <div key={e.id} data-testid={`admin-event-${e.id}`} className="flex flex-wrap items-center gap-4 rounded-[2rem] bg-cream-soft p-6 sm:gap-6">
                <div className="min-w-16 text-center">
                  <span className="block font-serif text-3xl italic text-sage">{new Date(`${e.date}T00:00:00`).getDate()}</span>
                  <span className="text-[0.6rem] font-semibold uppercase tracking-[0.2em] text-ink/40">{e.date.slice(5, 7)}/{e.date.slice(0, 4)}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-serif text-lg leading-snug">{e.title}</h3>
                  <p className="mt-1 text-xs text-ink/50">{e.time} · {e.location} · {e.price} · {e.spots} posti</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setForm({ ...e })} data-testid={`event-edit-${e.id}`} aria-label="Modifica" className="flex h-11 w-11 items-center justify-center rounded-full bg-sage-mist text-sage transition-transform duration-300 hover:scale-110">
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button onClick={() => deleteEvent(e.id)} data-testid={`event-delete-${e.id}`} aria-label="Elimina" className="flex h-11 w-11 items-center justify-center rounded-full bg-earth-mist text-earth transition-transform duration-300 hover:scale-110">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && tab === "prenotazioni" && (
        <div className="mt-10 space-y-4" data-testid="admin-bookings-list">
          {bookings.length === 0 && <p className="rounded-[2rem] bg-cream-soft p-8 text-center text-sm text-ink/50">Nessuna richiesta per ora.</p>}
          {bookings.map((b) => (
            <div key={b.id} data-testid={`booking-card-${b.id}`} className="rounded-[2rem] bg-cream-soft p-7">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <span className="rounded-full bg-sage-mist px-4 py-1.5 text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-sage-deep">{b.servizio}</span>
                  <h3 className="mt-3 font-serif text-xl">{b.nome}</h3>
                  <p className="mt-1 text-xs text-ink/50">
                    {b.email} {b.telefono && `· ${b.telefono}`} {b.data_preferita && `· Data preferita: ${b.data_preferita}`}
                  </p>
                </div>
                <button onClick={() => deleteBooking(b.id)} data-testid={`booking-delete-${b.id}`} aria-label="Elimina richiesta" className="flex h-11 w-11 items-center justify-center rounded-full bg-earth-mist text-earth transition-transform duration-300 hover:scale-110">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              {b.messaggio && <p className="mt-4 rounded-2xl bg-cream-dark/50 p-5 text-sm leading-relaxed text-ink/65">{b.messaggio}</p>}
              <p className="mt-3 text-[0.65rem] uppercase tracking-[0.2em] text-ink/35">
                Ricevuta il {new Date(b.created_at).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Admin;
